/**
 * Card primitive — TASK 7.1.
 *
 * Two shapes, both lifted verbatim from the mock:
 *
 * 1. `Card()` — the album/playlist COVER TILE repeated on /, /artists/:id,
 *    /genres/:id and /favorites (mock L167-180, L258-270, L327-337,
 *    L419-430): a `position:relative` wrapper holding a square gradient
 *    cover (`.sm-cover`, 3px ink border, 4px hard shadow, 14px radius,
 *    -1.2deg tilt), the Bangers initials, an optional status ribbon, the
 *    title/subtitle/year lines, the "⋯" overlay button and the ♥ marker.
 *
 * 2. `Panel()` — the bordered surface used for the storage meter, the
 *    settings profile block, the admin invite form and the sync tiles
 *    (mock L507, L516, L632, L652, L661, L694).
 *
 * The per-item gradient background and the dimmed opacity of an unavailable
 * item are the mock's only genuinely dynamic cover values (`{{ c.bg }}`,
 * `{{ c.dim }}`); they are applied through CSSOM (`styleProps`), never as a
 * `style=` string — see ./dom.js.
 *
 * The dim is applied to a dedicated gradient LAYER plus the initials — never
 * to `.sm-cover` itself. `opacity` on the cover would create a stacking
 * context that composites the §8.5 status ribbon down with it (a child cannot
 * opt back out with `opacity:1`), dropping the ribbon's yellow-on-ink text
 * below WCAG 1.4.3's 4.5:1 over bright cover gradients. The ribbon therefore
 * always renders at full opacity while the artwork underneath dims.
 *
 * §8.5 status treatment: `status` renders a RIBBON carrying a border-contrast
 * fill, an ICON and a TEXT label — the state is never signalled by colour
 * alone.
 *
 * @module
 */

import { AvailabilityBadge } from '../AvailabilityBadge.js';

import { append, classes, el } from './dom.js';

/**
 * @typedef {object} CardOptions
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} [year]
 * @property {string} initials Bangers glyph shown inside the cover
 * @property {string} href destination of the whole tile
 * @property {string} [background] CSS background value for the cover (dynamic)
 * @property {number} [opacity] 0-1; the mock dims unavailable items to .55.
 *   Applied to the gradient layer and the initials only — NOT to `.sm-cover`
 *   and NOT to the status ribbon (see the module header's WCAG 1.4.3 note)
 * @property {string} [coverUrl] the REAL cover art (`GET /api/art/:albumId`),
 *   layered OVER the gradient. Callers pass it ONLY for an album that has real
 *   art — the `covers/…` rule in {@link import('./format.js').albumArtUrl};
 *   art-less albums omit it and keep the gradient + initials. While the image
 *   loads (or if it 404s/breaks) the gradient underneath stays visible, and on
 *   a load error the image removes itself so the fallback is never regressed.
 * @property {import('../AvailabilityBadge.js').AvailabilityState} [status]
 *   §8.5 ribbon. TASK 8.1 (mock defects 8 + 10) moved the ribbon's copy and
 *   glyph into ../AvailabilityBadge.js so a card and a track row can never
 *   disagree about what "no longer available" looks like; this option is now
 *   just the state, and the badge component renders it.
 * @property {boolean} [favorite=false] renders the ♥ marker
 * @property {{ ariaLabel: string, onClick: (event: Event) => void, testId?: string }} [menu]
 *   the "⋯" overlay button (ui-contract §3 pattern `btn:Actions for *`)
 * @property {string} [testId]
 * @property {'left'|'right'} [tilt='left'] mock L169 (-1.2deg) vs L329 (+1deg)
 */

/**
 * @param {CardOptions} options
 * @returns {HTMLElement}
 */
export function Card(options) {
  const {
    title,
    subtitle,
    year,
    initials,
    href,
    background,
    opacity,
    coverUrl,
    status,
    favorite = false,
    menu,
    testId,
    tilt = 'left',
  } = options;

  const root = el('div', { className: 'sm-card', testId });

  const link = el('a', { className: 'sm-card__link', attrs: { href } });

  const cover = el('div', {
    className: classes('sm-cover', tilt === 'right' && 'sm-cover--tilt-right'),
  });

  const dim = opacity === undefined ? undefined : String(opacity);

  if (background !== undefined || dim !== undefined) {
    // The artwork layer. Its GEOMETRY is `.sm-cover__art` in components.css —
    // it is static, and the "dynamic values only" CSSOM rule in styles/README
    // means only `background` and `opacity` are assigned from here.
    append(
      cover,
      el('div', {
        className: 'sm-cover__art',
        styleProps: {
          ...(background === undefined ? {} : { background }),
          ...(dim === undefined ? {} : { opacity: dim }),
        },
      }),
    );
  }

  append(
    cover,
    el('span', {
      className: 'sm-cover__initials',
      text: initials,
      // Decorative duplication of the title the link already announces — the
      // same treatment ArtistsList.js gives its initials thumb (W10).
      attrs: { 'aria-hidden': 'true' },
      // The dim rides along so the glyph fades with the artwork behind it.
      // (`position: relative`, which lifts it above that layer, is in the CSS.)
      styleProps: dim === undefined ? {} : { opacity: dim },
    }),
  );

  if (coverUrl !== undefined) {
    // Real cover art layered OVER the gradient. Geometry (absolute, fill,
    // object-fit:cover) is `.sm-cover__art-img` in components.css. It is a LATER
    // DOM sibling than the gradient + initials but an EARLIER one than the
    // ribbon, so plain paint order (no z-index) stacks it above the fallback and
    // below the §8.5 ribbon / ♥ / ⋯ overlays. On a load error it removes itself,
    // revealing the gradient + initials — so a broken/404 cover never leaves an
    // album worse off than the art-less look.
    const art = el('img', {
      className: 'sm-cover__art-img',
      attrs: { src: coverUrl, alt: '', loading: 'lazy', decoding: 'async' },
      on: { error: () => art.remove() },
    });
    append(cover, art);
  }

  if (status !== undefined) {
    // icon + text, never colour alone (§8.5) — see ../AvailabilityBadge.js.
    // Rendered OUTSIDE the dimmed layer, at full opacity.
    append(cover, AvailabilityBadge({ state: status, variant: 'card' }));
  }

  append(
    link,
    cover,
    el('div', { className: 'sm-cover__title', text: title }),
    subtitle === undefined ? null : el('div', { className: 'sm-cover__sub', text: subtitle }),
    year === undefined ? null : el('div', { className: 'sm-cover__year', text: year }),
  );

  append(root, link);

  if (menu !== undefined) {
    append(
      root,
      el('button', {
        className: 'sm-btn sm-btn--icon sm-btn--icon-overlay',
        text: '⋯',
        ...(menu.testId === undefined ? {} : { testId: menu.testId }),
        attrs: { type: 'button', 'aria-label': menu.ariaLabel },
        on: { click: menu.onClick },
      }),
    );
  }

  if (favorite) {
    append(root, FavoriteMarker());
  }

  return root;
}

/**
 * The ♥ favourite marker on a cover tile (mock L179, L269, L429). Exported so
 * a grid can toggle it on an EXISTING card when the store changes (the
 * targeted repaint in views/browse/ArtistDetail.js) without re-rendering the
 * tile — the marker can therefore never drift from what {@link Card} builds.
 *
 * @returns {HTMLElement}
 */
export function FavoriteMarker() {
  return el('span', {
    className: 'sm-cover__fav',
    text: '♥',
    attrs: { 'aria-label': 'Favorited' },
  });
}

/**
 * Bordered surface — mock L507 (storage), L516/L661 (flat list panel),
 * L632 (settings profile), L652 (invite form), L694 (sync tile).
 *
 * @param {{
 *   variant?: 'default'|'flat'|'tile'|'profile'|'storage',
 *   tag?: string,
 *   testId?: string,
 *   className?: string|string[],
 *   children?: Array<Node|null|undefined|false>,
 * }} [options]
 * @returns {HTMLElement}
 */
export function Panel(options = {}) {
  const { variant = 'default', tag = 'div', testId, className, children = [] } = options;
  const VARIANT_CLASS = {
    default: undefined,
    flat: 'sm-panel--flat',
    tile: 'sm-panel--tile',
    profile: 'sm-panel--profile',
    storage: 'sm-panel--storage',
  };
  if (!(variant in VARIANT_CLASS)) throw new Error(`Panel: unknown variant "${String(variant)}"`);

  const node = el(tag, {
    className: classes(
      'sm-panel',
      VARIANT_CLASS[variant],
      ...(Array.isArray(className) ? className : classes(className)),
    ),
    testId,
  });
  return append(node, ...children);
}
